/**
 * Renders a Contentful `page` entry: its meta tags, optional breadcrumbs, and its sections in
 * order.
 *
 * Used by `pages/[...path].tsx` (published pages, at build) and `ContentfulPagePreview.tsx`
 * (drafts, in the dev build). The content model it knows is small: a page has a title, a meta
 * description, a `breadcrumbs` switch and a list of sections, and each section is a
 * `sectionLayout1` (text beside an image, `ContentfulSectionLayout1.tsx`) or a `sectionLayout2`
 * (centred text and a button over a background image, `ContentfulSectionLayout2.tsx`). A section of
 * any other content type renders nothing.
 *
 * Rules a change must keep:
 * - Every field read here or in a section must be in that component's `...Fields` list, which
 *   `ContentfulUtils.ts` → `getPageContent` passes to `Crawl` as `include`; a field left out of the
 *   list is pruned from the response.
 * - The canonical URL is `NEXT_PUBLIC_ENV_URL` + the entry's `path` (`MetaHead`). `company: false`
 *   leaves out the Organization JSON-LD block, as on the other Contentful-driven pages.
 *
 * Adding `?debug=true` to the URL prints the entry's JSON above the page.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection } from "@ryerson/frontend.layout";
import { Breadcrumb } from "@ryerson/frontend.navigation";
import { useApplication } from "@ryerson/frontend.application";
import ContentfulSectionLayout1, { IContentfulSectionLayout1 } from "./ContentfulSectionLayout1";
import ContentfulSectionLayout2, { IContentfulSectionLayout2 } from "./ContentfulSectionLayout2";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import { Code } from "@ryerson/frontend.typography";
import { LanguageContent } from "@ryerson/frontend.common";
import ContentfulUtils from "./ContentfulUtils";

/** Fields `Crawl` adds to every entry it returns, from the entry's `sys` metadata. */
export interface IContentfulBaseFields {
	contentful_id: string;
	// The entry's locale, for example `en-US`.
	node_locale: string;
	// The Contentful content type id, for example `sectionLayout1`.
	content_type: string;
}

/**
 * A Contentful `page` entry, as `ContentfulUtils.ts` → `getPageContent` returns it per language.
 */
export interface IContentfulPage extends IContentfulBaseFields {
	// URL path from the site root, matched exactly by `getPageContent`.
	path: string;
	// The companies the page is built for. Contentful returns a list
	// (`pages/[...path].tsx` → `IContentfulPageStaticPath` reads it as `string[]`).
	website: string;
	// Whether to show the breadcrumb bar above the first section.
	breadcrumbs: boolean;
	metaDescription: string;
	title: string;
	sections: (IContentfulSectionLayout1 | IContentfulSectionLayout2)[];
}

/** The `page` fields `getPageContent` asks `Crawl` to keep. */
export const ContentfulPageFields = [
	"path",
	"website",
	"breadcrumbs",
	"metaDescription",
	"title",
	"sections",
];

/** Props for {@link ContentfulPage}: one `page` entry as `getPageContent` returns it. */
export interface ContentfulPageProps {
	// The entry in every language; the visitor's `localization.language` picks one.
	contentfulPage: LanguageContent<IContentfulPage>;
}

/** Renders one Contentful `page` in the visitor's language; see the file header. */
const ContentfulPage: React.FC<ContentfulPageProps> = ({ contentfulPage }) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const debug = typeof window !== "undefined" && window.location.search.includes("debug=true");

	const content = contentfulPage[language];

	// The breadcrumb bar uses the first section's `type`, or `tertiary` when there is none.
	const firstSectionType = ContentfulUtils.getType(
		content?.sections?.[0]?.type?.toLowerCase() || "tertiary"
	);

	const metaContent: MetaContent = {
		title: content.title,
		description: content.metaDescription,
		url: content.path,
		company: false,
	};

	return (
		<>
			<MetaHead content={metaContent} />
			{debug && (
				<ContentSection>
					<Code>{JSON.stringify(content, null, 4)}</Code>
				</ContentSection>
			)}
			{content.breadcrumbs && (
				<ContentSection type={firstSectionType}>
					<Breadcrumb type={firstSectionType} />
				</ContentSection>
			)}
			{content.sections?.map((section, index) => {
				if (section.content_type === "sectionLayout1") {
					const contentfulSectionLayout1: IContentfulSectionLayout1 =
						section as IContentfulSectionLayout1;
					return (
						<ContentfulSectionLayout1 content={contentfulSectionLayout1} key={index} />
					);
				}
				if (section.content_type === "sectionLayout2") {
					const contentfulSectionLayout2: IContentfulSectionLayout2 =
						section as IContentfulSectionLayout2;
					return (
						<ContentfulSectionLayout2 content={contentfulSectionLayout2} key={index} />
					);
				}
				return null;
			})}
		</>
	);
};

export default ContentfulPage;
